import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {getLanguageLabel} from 'src/components/settings/language-support';
import {useSelector, useDispatch} from 'react-redux';
import {userFormInitialValue} from 'src/utils/referrer-portal';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import type {UserDetails} from 'src/types/referrer-portal';
import {selectUserDetails} from 'src/selectors/referrer-portal';
import {languageMenuOptions} from 'src/constants/language-support';
import {useHistory} from 'src/rerouter/hooks';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Avatar} from '@spaced-out/ui-design-system/lib/components/Avatar';
import {ButtonTextSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {CustomDropdown} from 'src/components/lib/custom-dropdown/custom-dropdown';
import {size200} from '@spaced-out/ui-design-system/lib/styles/variables/_size';

import css from './account-bar.css';
import {type MenuOption} from '@spaced-out/ui-design-system';


export const AccountBar = () => {
  const dispatch = useDispatch();
  const [profileUrl, setProfileUrl] = React.useState<any>(null);
  const account: UserDetails | null | undefined =
    useSelector(selectUserDetails) ?? userFormInitialValue;
  const history = useHistory();
  const {t, updateLanguage, getCurrentLanguage} = useLabelTranslation();
  const [activeMenu, setActiveMenu] = React.useState('custom');
  const initialLanguage = getCurrentLanguage({
    supportBrowserLanguage: false,
    supportDefaultLanguage: false,
    supportLocalStorageLanguage: true,
    supportApiSelectedLanguage: false,
  });
  const [selectedLanguage, setSelectedLanguage] = React.useState({
    key: initialLanguage,
    label: getLanguageLabel(initialLanguage),
  });

  const accountMenuOptions = [
    {
      key: 'language_support',
      label: `${t('Language', 'Language')} : ${selectedLanguage.label}`,
      iconLeft: 'language',
      iconRight: 'chevron-right',
    },
    {
      key: 'signout',
      label: t('Sign_out', 'Sign out'),
      iconLeft: 'arrow-right-from-bracket',
    },
  ];

  const updatedLanguageOptions = languageMenuOptions.map((option) => ({
    ...option,
    iconRight: option.key === selectedLanguage.key ? 'check' : '',
  }));

  const onLanguageSelect = async (option: MenuOption) => {
    await updateLanguage({
      language: option.key,
      persistLanguage: true,
      sendUpdatedLanguageToAPI: false,
    });
    setSelectedLanguage({
      key: option.key,
      label: getLanguageLabel(option.key),
    });
    setActiveMenu('custom');
  };

  const handleAccountOptionClick = (option: MenuOption) => {
    if (option.key === 'signout') {
      history.push('/signout');
    } else {
      setActiveMenu('language');
    }
  };

  React.useEffect(() => {
    if (account?.profile_picture_url) {
      dispatch(reduxApi.get('user/picture')).then((data) =>
        setProfileUrl(data),
      );
    }
  }, [account?.profile_picture_url]);
  return (
    <CustomDropdown
      anchorPosition="right"
      ariaLabel={account?.name}
      menu={{
        isFluid: false,
        menuDisabled: false,
        options:
          activeMenu === 'custom' ? accountMenuOptions : updatedLanguageOptions,
        width: size200,
        selectedKeys: [selectedLanguage.key],
        classNames: {wrapper: css.menuShift},
        header: activeMenu === 'language' && (
          <div className={css.menuHeader}>
            <Button
              type="ghost"
              iconLeftName="chevron-left"
              size="small"
              onClick={() => setActiveMenu('custom')}
            />
            <ButtonTextSmall>
              {t('Choose_Language', 'Choose Language')}
            </ButtonTextSmall>
          </div>
        ),
      }}
      tooltip={{
        body: account?.name,
        classNames: {tooltip: css.toolTip},
      }}
      onOptionSelect={
        activeMenu === 'language' ? onLanguageSelect : handleAccountOptionClick
      }
      tabIndex={0}
      classNames={{
        dropdownContainer: css.collapsedDropdownContainer,
        wrapper: css.accountContainer,
      }}
    >
      <div className={css.avatarWrap}>
        <Avatar text={account?.name} ring={true} imageSrc={profileUrl ?? ''} />
      </div>
    </CustomDropdown>
  );
};
